/**
 * 표 구조 편집 시 커스텀 셀 attr(rowHeight, verticalAlignment) 유실을 막는 플러그인.
 *
 * 문제:
 *  BlockNote의 행/열 추가·삭제(ExtendButton)와 행/열 드래그 재정렬은 내부적으로
 *  `editor.updateBlock(block, ...)`로 표 블록 전체를 재작성한다. 이때 사용하는 block은
 *  `nodeToBlock`(contentNodeToTableContent)에서 온 것인데, 이 변환은 고정된 prop 집합
 *  (colspan/rowspan/backgroundColor/textColor/textAlignment)만 cell.props에 담는다.
 *  → rowHeight·verticalAlignment 같은 커스텀 글로벌 attribute는 그 시점에 누락되고,
 *    updateBlock→blockToNode 재작성으로 PM 노드에서도 사라진다(셀 기본값으로 초기화).
 *
 * 해결:
 *  appendTransaction에서 "직전(old) 상태의 셀 attr"과 "현재(new) 상태"를 비교해, 구조
 *  편집으로 기본값이 된 셀에 직전 값을 다시 채운다. (row, 행 내 cell) 인덱스로 매칭한다.
 *
 *  - 사용자가 직접 리사이즈/정렬한 경우: 새 값이 non-default라 복원이 끼어들지 않는다.
 *  - 새로 추가된 행/열의 셀: 직전 값이 없으므로 그대로 기본값(정상).
 *  - self-terminating: 복원 후 재실행 시 new 값이 채워져 더 복원할 게 없다.
 */

import { Plugin, PluginKey } from "prosemirror-state";
import type { Node as PMNode } from "prosemirror-model";

export const tableCellAttrPreserveKey = new PluginKey(
  "lumirTableCellAttrPreserve",
);

/** 보존 대상 attr과 "기본값"(이 값이면 의미 없음 = 보존/복원 제외). */
const PRESERVED_ATTRS: { name: string; default: unknown }[] = [
  { name: "rowHeight", default: null },
  { name: "verticalAlignment", default: "top" },
];

function isMeaningful(attrName: string, value: unknown, def: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  return value !== def;
}

/** 셀의 보존 대상 attr 중 의미 있는 값만 추린다. 없으면 null. */
function meaningfulAttrs(node: PMNode): Record<string, unknown> | null {
  let out: Record<string, unknown> | null = null;
  for (const { name, default: def } of PRESERVED_ATTRS) {
    const v = node.attrs?.[name];
    if (isMeaningful(name, v, def)) {
      (out ??= {})[name] = v;
    }
  }
  return out;
}

/**
 * 문서에서 table 블록 id → "row:cell" → {attr:value} 맵을 만든다.
 * id는 blockContainer의 id attr(편집을 가로질러 유지됨)로 매칭한다.
 */
function collectCellAttrs(
  doc: PMNode,
): Map<string, Map<string, Record<string, unknown>>> {
  const result = new Map<string, Map<string, Record<string, unknown>>>();

  doc.descendants((node) => {
    if (node.type.name !== "blockContainer") {
      return undefined;
    }
    const id = node.attrs?.id as string | undefined;
    const table = node.firstChild;
    if (!id || table?.type.name !== "table") {
      // 표가 아닌 블록도 children에 표를 가질 수 있으므로 계속 descend.
      return undefined;
    }
    const cells = new Map<string, Record<string, unknown>>();
    let rowIndex = 0;
    table.forEach((rowNode) => {
      if (rowNode.type.name === "tableRow") {
        let cellIndex = 0;
        rowNode.forEach((cellNode) => {
          const attrs = meaningfulAttrs(cellNode);
          if (attrs) {
            cells.set(`${rowIndex}:${cellIndex}`, attrs);
          }
          cellIndex++;
        });
        rowIndex++;
      }
    });
    if (cells.size > 0) {
      result.set(id, cells);
    }
    // 표 셀 내부엔 더 깊은 표가 없으므로 하위 순회는 불필요.
    return false;
  });

  return result;
}

export function tableCellAttrPreserve(): Plugin {
  return new Plugin({
    key: tableCellAttrPreserveKey,
    appendTransaction(transactions, oldState, newState) {
      if (!transactions.some((tr) => tr.docChanged)) {
        return null;
      }
      const oldMap = collectCellAttrs(oldState.doc);
      if (oldMap.size === 0) {
        return null;
      }

      const tr = newState.tr;
      let changed = false;

      // new 문서를 순회하며 구조 편집으로 기본값이 된 셀을 old 값으로 복원.
      let curTableCells: Map<string, Record<string, unknown>> | null = null;
      let curRowIndex = -1;
      let curCellIndex = 0;

      newState.doc.descendants((node, pos) => {
        const name = node.type.name;
        if (name === "blockContainer") {
          const id = node.attrs?.id as string | undefined;
          const table = node.firstChild;
          if (id && table?.type.name === "table" && oldMap.has(id)) {
            curTableCells = oldMap.get(id)!;
            curRowIndex = -1;
          } else {
            curTableCells = null;
          }
          return undefined; // 항상 descend(중첩 표 대응)
        }
        if (!curTableCells) {
          return undefined;
        }
        if (name === "tableRow") {
          curRowIndex++;
          curCellIndex = 0;
          return undefined;
        }
        if (name === "tableCell" || name === "tableHeader") {
          const wanted = curTableCells.get(`${curRowIndex}:${curCellIndex}`);
          curCellIndex++;
          if (wanted) {
            const patch: Record<string, unknown> = {};
            let needs = false;
            for (const { name: attrName, default: def } of PRESERVED_ATTRS) {
              if (!(attrName in wanted)) {
                continue;
              }
              const cur = node.attrs?.[attrName];
              // 현재 값이 기본값/빈값일 때만 복원(사용자가 새로 지정한 값은 보존).
              if (cur === null || cur === undefined || cur === def) {
                patch[attrName] = wanted[attrName];
                needs = true;
              }
            }
            if (needs) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...patch });
              changed = true;
            }
          }
          return false; // 셀 내부는 순회 불필요
        }
        return undefined;
      });

      return changed ? tr : null;
    },
  });
}
